import Vue from 'vue'
import { quoteNumber } from './quoteDisplay'

// PC display quotes only. No timers, orders, chart points or list membership live here.
export const stockQuoteFeed = Vue.observable({ rows: {} })
let sequence = 0
export const quoteRequestStamp = () => ++sequence

export function stockQuoteKey(value, market = '') {
  value = String(value || '').trim().replace(/^(?:s_)?r_|^t_(?:s_)?|^s_/i, '')
  let match = value.match(/^(sh|sz|bj)(\d{6})$/i) || value.match(/^(\d{6})\.(sh|sz|bj)$/i)
  if (match) {
    const key = /^\d/.test(match[1]) ? match[2].toLowerCase() + match[1] : match[1].toLowerCase() + match[2]
    return /^(?:sh000(?:001|300|688)|sz399\d{3}|bj899050)$/.test(key) ? '' : key
  }
  if (/^hk\d{5}$/i.test(value)) return value.toLowerCase()
  if (market === 'hk' && /^\d{1,5}(?:\.HK)?$/i.test(value)) return 'hk' + value.replace(/\.HK$/i, '').padStart(5, '0')
  if (/^us[A-Za-z0-9]/.test(value) || market === 'us') {
    // Raw US tickers such as USB/USFD are not an "us" market prefix.
    const ticker = value.replace(/^us/, '').replace(/\.(?:USA?|N|OQ|AM)$/i, '').toUpperCase()
    return /^[A-Z0-9][A-Z0-9._-]{0,39}$/.test(ticker) ? 'us' + ticker : ''
  }
  if (market === 'cn' && /^\d{6}$/.test(value)) return stockQuoteKey((/^[489]/.test(value) ? 'bj' : /^6/.test(value) ? 'sh' : 'sz') + value)
  return ''
}

const timeOf = value => {
  const digits = String(value || '').replace(/\D/g, '')
  return /^20\d{12}/.test(digits) ? Number(digits.slice(0, 14)) : 0
}

export function publishStockQuote(code, values, stamp, time = 0) {
  const key = stockQuoteKey(code)
  if (!key || !(quoteNumber(values.price) > 0)) return
  const previous = stockQuoteFeed.rows[key] || { values: {}, clocks: {} }
  const next = { values: { ...previous.values }, clocks: { ...previous.clocks } }
  let changed = false
  for (const [field, value] of Object.entries(values)) {
    if (field === 'name' ? !String(value || '').trim() : quoteNumber(value) == null) continue
    const old = previous.clocks[field]
    // Compare provider time when both sides have it; otherwise use request-start order.
    // Do not permanently pin an old direct quote after its trade panel has closed.
    if (old && (time && old.time ? time < old.time || (time === old.time && stamp < old.stamp) : stamp < old.stamp)) continue
    next.values[field] = value
    next.clocks[field] = { stamp, time: time || (old && old.time) || 0 }
    changed = true
  }
  if (changed) Vue.set(stockQuoteFeed.rows, key, next)
}

export function publishTencentQuote(code, row, stamp) {
  const key = stockQuoteKey(code)
  if (!key || !Array.isArray(row) || !(quoteNumber(row[3]) > 0)) return
  const simple = /^s_|^t_s_/i.test(code)
  const values = { name: row[1], price: row[3], delta: row[simple ? 4 : 31], change: row[simple ? 5 : 32] }
  if (!simple) {
    const cn = /^(sh|sz|bj)/.test(key), hk = /^hk/.test(key)
    Object.assign(values, { high: row[33], low: row[34], open: row[5], close: row[4],
      volume: row[36], amount: row[37], amplitude: row[43], marketValue: row[45], floatValue: row[44],
      turnover: row[hk ? 59 : 38], pe: row[cn ? 53 : hk ? 57 : 39], pb: row[cn ? 46 : hk ? 58 : 51],
      staticPe: cn ? row[39] : null,
      ratio: row[49], bookrate: cn ? row[74] : null,
      totalShares: cn && quoteNumber(row[73]) != null ? Number(row[73]) / 10000 : null,
      floatShares: cn && quoteNumber(row[76]) != null ? Number(row[76]) / 10000 : null,
      maxprice: cn ? row[47] : null, minprice: cn ? row[48] : null })
  }
  publishStockQuote(key, values, stamp, simple ? 0 : timeOf(row[30]))
}

const first = (row, keys) => keys.map(key => row[key]).find(value => value != null && value !== '')
const keyOf = (row, market) => stockQuoteKey(row.full_code || row.symbol || row.code, market)

export function publishStockRows(rows, market, stamp, shape = '') {
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== 'object') continue
    const key = keyOf(row, market)
    if (!key) continue
    const leader = shape === 'leader'
    const values = leader ? { name: row.nzg_name, price: row.nzg_zxj, change: row.nzg_zdf } : {
      name: first(row, ['prod_name', 'name']), price: first(row, ['last_px', 'zxj', 'price']),
      delta: first(row, ['px_change', 'zd', 'delta']), change: first(row, ['px_change_rate', 'zdf', 'increase', 'change']),
      high: row.high, low: row.low, open: row.open, close: first(row, ['yclose', 'close']),
      amount: first(row, market === 'hk' ? ['turnover', 'amount'] : ['business_balance', 'amount']),
      volume: first(row, ['volume', 'vol']), amplitude: first(row, ['amplitude', 'zf']),
      turnover: first(row, market === 'hk' ? ['hsl', 'exchangerate'] : ['exchange', 'exchangerate', 'hand_rate']),
      pe: first(row, market === 'cn' ? ['pe_rate', 'pe2'] : ['pe_ttm', 'pe_rate', 'pe']), staticPe: market === 'cn' ? row.pe : null, pb: first(row, ['pn', 'market_rate', 'pb']),
      marketValue: first(row, ['zsz', 'total_value', 'marketvalue', 'marketValue']),
      floatValue: first(row, ['ltsz', 'circulation_value', 'flowmarketvalue', 'floatValue']),
      ratio: first(row, ['lb', 'volratio']), bookrate: row.wb, totalShares: row.total_stock, floatShares: row.total_mstock,
      maxprice: row.maxprice, minprice: row.minprice
    }
    const quoteTime = first(row, ['quote_time', 'data_timestamp', 'time'])
    const date = row.date || row.data_datestamp
    const timestamp = date && /^\d{6}$/.test(String(quoteTime)) ? String(date) + quoteTime : quoteTime
    publishStockQuote(key, values, stamp, timeOf(timestamp))
  }
}

export function sharedStockRow(row, market, shape = '') {
  const quote = stockQuoteFeed.rows[keyOf(row, market)]
  if (!quote) return row
  const values = quote.values, result = { ...row }
  const fields = shape === 'leader' ? { name: ['nzg_name'], price: ['nzg_zxj'], change: ['nzg_zdf'] }
    : {
      name: ['name', 'prod_name'], price: ['price', 'last_px', 'zxj'], delta: ['delta', 'px_change', 'zd'],
      change: ['change', 'px_change_rate', 'zdf', 'increase'], high: ['high'], low: ['low'], open: ['open', 'todayOpen'], close: ['close', 'yclose', 'yesClose'],
      volume: ['volume', 'vol'], amount: ['amount', 'business_balance'], amplitude: ['amplitude', 'zf', 'zhenfu'],
      turnover: ['turnover', 'hsl', 'exchangerate', 'hand_rate'], pe: ['pe', 'pe2', 'pe_ttm', 'pe_rate'], pb: ['pb', 'pn', 'market_rate'],
      marketValue: ['marketValue', 'marketvalue', 'zsz', 'total_value'], floatValue: ['floatValue', 'flowmarketvalue', 'ltsz', 'circulation_value'],
      ratio: ['ratio', 'lb', 'volratio'], bookrate: ['bookrate', 'wb'], totalShares: ['total_stock'], floatShares: ['total_mstock'],
      maxprice: ['maxprice'], minprice: ['minprice']
    }
  for (const [field, aliases] of Object.entries(fields)) {
    if (values[field] == null) continue
    for (const alias of aliases) result[alias] = values[field]
  }
  if (shape === 'hk-board' && values.amount != null) result.turnover = values.amount
  if (shape === 'cn-trade') {
    if (values.staticPe != null) result.pe = values.staticPe
    if (values.volume != null) result.vol = Number(values.volume) * 100
    if (values.amount != null) result.amount = Number(values.amount) * 10000
  }
  return result
}
export const sharedStockRows = (rows, market, shape) => (rows || []).map(row => sharedStockRow(row, market, shape))

// Only whitelisted PC stock responses participate; business/index/chart responses do not.
export function publishBackendQuotes(config, body) {
  const action = String(config.url || '').match(/(?:^|\/)vexe\/v1\/stock\/(\w+)(?:\?|$)/)
  if (!action) return
  let params = config.data || config.params || {}
  if (typeof params === 'string') {
    try { params = JSON.parse(params) } catch (_) { return }
  }
  if (!params || typeof params !== 'object') return
  const stamp = config.quoteStamp || quoteRequestStamp(), name = action[1]
  if (name === 'baseData') {
    const row = body && body.stock && body.stock[0]
    if (row) publishStockRows([{ ...row, full_code: params.code }], /^hk/i.test(params.code) ? 'hk' : /^us/i.test(params.code) ? 'us' : 'cn', stamp)
  } else if (['stockListByCate', 'up', 'down', 'cje', 'hsl'].includes(name)) {
    publishStockRows(body && body.data, 'cn', stamp)
  } else if (name === 'board') publishStockRows(body && body.lists, 'cn', stamp)
  else if (name === 'marketBoard') publishStockRows(body && body.data && body.data.rank_list, params.market, stamp)
  else if (name === 'stockGGRank' || name === 'stockMGRank') {
    const market = name === 'stockGGRank' ? 'hk' : 'us'
    for (const [key, rows] of Object.entries(body || {})) {
      if (Array.isArray(rows) && /^(advance|decline|amount)/.test(key)) publishStockRows(rows, market, stamp)
      if (key === 'industry' && Array.isArray(rows)) publishStockRows(rows, market, stamp, 'leader')
    }
  } else if (name === 'stockGGHot' && Array.isArray(body)) {
    const market = Number(params.plate) === 400 ? 'hk' : 'us'
    if (params.code) publishStockRows(body, market, stamp)
    else publishStockRows(body.filter(row => row && typeof row === 'object').map(row => ({ code: row.fc, nzg_zxj: row.nzg_zxj, nzg_zdf: row.fzdf, nzg_name: row.fn })), market, stamp, 'leader')
  }
}
